// OWNER: FRONTEND-ADMIN. Editor for every part of a CampaignScript (SPEC §15.5) and a read-only view for setters.
import type { ComponentChildren } from 'preact';
import type { CampaignScript, Country, EmailTemplate, ObjectionCard, ScriptBlock, VoicemailScript } from '../../../src/shared/api';
import { OBJECTIONS } from '../../../src/shared/api';
import { Field, ListEditor, inputValue } from './ui';
import { CANADA_NO_DROPS, wordCount } from './validate';

const OBJECTION_LABELS: Record<string, string> = {
  no_time: 'No time', not_interested: 'Not interested', send_info: 'Send info', have_someone: 'Have someone',
  too_expensive: 'Too expensive', bad_experience: 'Bad experience', ai_skeptic: 'AI skeptic', need_partner: 'Need partner',
  call_later: 'Call later', other: 'Other',
};

const SECTIONS = [
  ['summary', 'Summary'], ['openers', 'Openers'], ['reason', 'Reason'], ['questions', 'Questions'], ['pitch', 'Pitch & ask'],
  ['gatekeeper', 'Gatekeeper'], ['objections', 'Objections'], ['vm-live', 'Live VMs'], ['vm-drop', 'Drop scripts'],
  ['emails', 'Emails'], ['disqualifiers', 'Disqualifiers'], ['compliance', 'Compliance'],
] as const;

/** ~150 spoken words per minute. */
const secondsFor = (text: string) => Math.round(wordCount(text) / 2.5);

function Section(props: { id: string; title: string; hint?: ComponentChildren; children: ComponentChildren }) {
  return (
    <section class="adm-section card" id={`script-${props.id}`}>
      <h3>{props.title}</h3>
      {props.hint ? <div class="adm-sub" style="margin-top:-4px">{props.hint}</div> : null}
      {props.children}
    </section>
  );
}

function TextArea(props: { value: string; onChange: (v: string) => void; rows?: number; placeholder?: string }) {
  return <textarea rows={props.rows ?? 3} value={props.value} placeholder={props.placeholder} onInput={(e) => props.onChange(inputValue(e))} />;
}

function BlockList(props: { items: ScriptBlock[]; onChange: (b: ScriptBlock[]) => void; addLabel: string; labelHint: string; textRows?: number }) {
  return (
    <ListEditor<ScriptBlock>
      items={props.items} onChange={props.onChange} addLabel={props.addLabel}
      make={() => ({ label: '', text: '' })}
      title={(b) => b.label || '(no label)'}
      render={(b, update) => (
        <div class="stack">
          <Field label="Label"><input value={b.label} placeholder={props.labelHint} onInput={(e) => update({ ...b, label: inputValue(e) })} /></Field>
          <Field label="Text"><TextArea value={b.text} rows={props.textRows} onChange={(text) => update({ ...b, text })} /></Field>
        </div>
      )}
    />
  );
}

function VoicemailList(props: { items: VoicemailScript[]; onChange: (v: VoicemailScript[]) => void; drop: boolean }) {
  const limit = props.drop ? 30 : 25;
  return (
    <ListEditor<VoicemailScript>
      items={props.items} onChange={props.onChange} addLabel={props.drop ? 'Add drop script' : 'Add live voicemail'}
      make={() => ({ situation: '', label: '', text: props.drop ? 'Hi, this is {setter} with Valistix Inc. … Call me back at {callback}.' : '' })}
      title={(v) => `${v.label || '(no label)'}${v.situation ? ` · ${v.situation}` : ''}`}
      render={(v, update) => {
        const secs = secondsFor(v.text);
        return (
          <div class="stack">
            <div class="adm-grid">
              <Field label="Situation" hint="first, second, no_show…"><input value={v.situation} list="vm-situations" onInput={(e) => update({ ...v, situation: inputValue(e).trim() })} /></Field>
              <Field label="Label"><input value={v.label} onInput={(e) => update({ ...v, label: inputValue(e) })} /></Field>
            </div>
            <Field label="Script" hint={<span class={secs > limit ? 'chip chip-warn' : ''}>{wordCount(v.text)} words ≈ {secs} s spoken (limit {limit} s)</span>}>
              <TextArea value={v.text} rows={3} onChange={(text) => update({ ...v, text })} />
            </Field>
          </div>
        );
      }}
    />
  );
}

export function ScriptEditor(props: { value: CampaignScript; onChange: (s: CampaignScript) => void; country?: Country | 'MIXED' }) {
  const s = props.value;
  const set = <K extends keyof CampaignScript>(k: K, v: CampaignScript[K]) => props.onChange({ ...s, [k]: v });
  return (
    <div class="stack" style="gap:12px">
      <datalist id="vm-situations"><option value="first" /><option value="second" /><option value="no_show" /></datalist>
      <datalist id="email-situations"><option value="after_vm" /><option value="after_call" /></datalist>
      <div class="adm-toolbar">
        <span class="eyebrow">Jump to</span>
        {SECTIONS.map(([id, label]) => (
          <button type="button" class="adm-link" style="font-size:13px" onClick={() => document.getElementById(`script-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>{label}</button>
        ))}
      </div>
      <div class="adm-muted-box">
        Placeholders: <code>{'{setter}'}</code> (setter's full name on US leads, first name on Canadian leads), <code>{'{business}'}</code>, <code>{'{dm_name}'}</code>,{' '}
        <code>{'{observation}'}</code> (only shown when a setter verified it), <code>{'{callback}'}</code> (main callback number).
        Emails also get <code>{'{first_name}'}</code>, <code>{'{calendly_link}'}</code> and <code>{'{mailing_address}'}</code> (required in every email: CASL, CAN-SPAM). Drop scripts may only use <code>{'{setter}'}</code> and <code>{'{callback}'}</code>.
      </div>

      <Section id="summary" title="Summary" hint="One line: who we call and why.">
        <TextArea value={s.summary} rows={2} onChange={(v) => set('summary', v)} />
      </Section>

      <Section id="openers" title="Openers" hint="Permission/context openers. Never a covert test call, never “bad time?”.">
        <BlockList items={s.openers} onChange={(v) => set('openers', v)} addLabel="Add opener" labelHint="Context (observation)" />
      </Section>

      <Section id="reason" title="Reason for the call" hint="Said in the first ~15 seconds.">
        <TextArea value={s.reason} rows={2} onChange={(v) => set('reason', v)} />
      </Section>

      <Section id="questions" title="Problem questions">
        <BlockList items={s.questions} onChange={(v) => set('questions', v)} addLabel="Add question" labelHint="Missed calls" textRows={2} />
      </Section>

      <Section id="pitch" title="Pitch and ask">
        <Field label="Pitch" hint="One or two sentences in problem language."><TextArea value={s.pitch} rows={2} onChange={(v) => set('pitch', v)} /></Field>
        <Field label="Ask" hint="Two specific times, next day first."><TextArea value={s.ask} rows={2} onChange={(v) => set('ask', v)} /></Field>
      </Section>

      <Section id="gatekeeper" title="Gatekeeper lines">
        <BlockList items={s.gatekeeper} onChange={(v) => set('gatekeeper', v)} addLabel="Add gatekeeper line" labelHint="Who handles the website?" textRows={2} />
      </Section>

      <Section id="objections" title="Objection cards" hint="8–12 cards. Setters open them with O then the number, in this order.">
        <ListEditor<ObjectionCard>
          items={s.objections} onChange={(v) => set('objections', v)} addLabel="Add objection card"
          make={() => ({ key: '', label: '', category: 'other', response: '' })}
          title={(o) => `${o.label || '(no label)'} · ${OBJECTION_LABELS[o.category] ?? o.category}`}
          render={(o, update) => (
            <div class="stack">
              <div class="adm-grid">
                <Field label="Button label" hint={<span class={o.label.length > 24 ? 'chip chip-bad' : ''}>{o.label.length}/24</span>}>
                  <input value={o.label} onInput={(e) => update({ ...o, label: inputValue(e) })} />
                </Field>
                <Field label="Key" hint="unique, e.g. too_expensive">
                  <input value={o.key} onInput={(e) => update({ ...o, key: inputValue(e).trim().toLowerCase().replace(/[^a-z0-9_]+/g, '_') })} />
                </Field>
                <Field label="Category">
                  <select value={o.category} onChange={(e) => update({ ...o, category: inputValue(e) as ObjectionCard['category'] })}>
                    {OBJECTIONS.map((c) => <option value={c}>{OBJECTION_LABELS[c] ?? c}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Response" hint="1–3 short sentences. Acknowledge, don't argue."><TextArea value={o.response} rows={2} onChange={(response) => update({ ...o, response })} /></Field>
              <Field label="Follow-up question (optional)">
                <input value={o.followUpQuestion ?? ''} onInput={(e) => { const v = inputValue(e); update({ ...o, followUpQuestion: v ? v : undefined }); }} />
              </Field>
            </div>
          )}
        />
      </Section>

      <Section id="vm-live" title="Live voicemail scripts" hint="Read live by the setter. ≤ 25 s; open with name + Valistix, end with {callback}.">
        <VoicemailList items={s.voicemailsLive} onChange={(v) => set('voicemailsLive', v)} drop={false} />
      </Section>

      <Section id="vm-drop" title="Voicemail drop scripts" hint={props.country === 'CA' ? 'Not used on Canadian campaigns.' : 'Setters record these in their own voice. Open with “Hi, this is {setter} with Valistix Inc.” and state {callback}. Max 30 s.'}>
        {props.country === 'CA' ? (
          <>
            <div class="adm-muted-box"><b>{CANADA_NO_DROPS}</b> Pre-recorded voicemails are never left on Canadian numbers, so a Canadian campaign has no drop scripts and setters see this line instead of a recorder.</div>
            {s.voicemailsDrop.length ? <VoicemailList items={s.voicemailsDrop} onChange={(v) => set('voicemailsDrop', v)} drop /> : null}
          </>
        ) : <VoicemailList items={s.voicemailsDrop} onChange={(v) => set('voicemailsDrop', v)} drop />}
      </Section>

      <Section id="emails" title="Follow-up emails" hint="Reference copy only: the mailto button that sent these was retired on 2026-10-03. Setters now use Send email (drafted from the call, sent from their Valistix address with an unsubscribe link, never after a voicemail).">
        <ListEditor<EmailTemplate>
          items={s.emails} onChange={(v) => set('emails', v)} addLabel="Add email"
          make={() => ({ situation: '', subject: '', body: '' })}
          title={(e) => `${e.situation || '(no situation)'} · ${e.subject || '(no subject)'}`}
          render={(e, update) => (
            <div class="stack">
              <div class="adm-grid">
                <Field label="Situation"><input value={e.situation} list="email-situations" onInput={(ev) => update({ ...e, situation: inputValue(ev).trim() })} /></Field>
                <Field label="Subject"><input value={e.subject} onInput={(ev) => update({ ...e, subject: inputValue(ev) })} /></Field>
              </div>
              <Field label="Body"><TextArea value={e.body} rows={6} onChange={(body) => update({ ...e, body })} /></Field>
            </div>
          )}
        />
      </Section>

      <Section id="disqualifiers" title="Disqualifiers" hint="“Not a fit” signals (chain/MSO, closed, already has it…).">
        <StringList items={s.disqualifiers} onChange={(v) => set('disqualifiers', v)} addLabel="Add disqualifier" />
      </Section>

      <Section id="compliance" title="Compliance notes" hint="Pinned at the bottom of the dialer: what this campaign must never say or do.">
        <StringList items={s.complianceNotes} onChange={(v) => set('complianceNotes', v)} addLabel="Add compliance note" />
      </Section>
    </div>
  );
}

function StringList(props: { items: string[]; onChange: (v: string[]) => void; addLabel: string }) {
  return (
    <ListEditor<string>
      items={props.items} onChange={props.onChange} addLabel={props.addLabel}
      make={() => ''}
      title={(x) => (x.length > 70 ? x.slice(0, 70) + '…' : x || '(empty)')}
      render={(x, update) => <input value={x} onInput={(e) => update(inputValue(e))} />}
    />
  );
}

/** Read-only script for setters (and admins previewing). */
export function ScriptView(props: { script: CampaignScript }) {
  const s = props.script;
  const blocks = (title: string, items: Array<{ label: string; text: string }>) => items.length === 0 ? null : (
    <div class="card stack"><h3>{title}</h3>{items.map((b) => <div><div class="eyebrow">{b.label}</div><div style="white-space:pre-wrap">{b.text}</div></div>)}</div>
  );
  const text = (title: string, t: string) => !t ? null : <div class="card stack"><h3>{title}</h3><div style="white-space:pre-wrap">{t}</div></div>;
  return (
    <div class="stack" style="gap:10px">
      {text('Summary', s.summary)}
      {blocks('Openers', s.openers)}
      {text('Reason', s.reason)}
      {blocks('Questions', s.questions)}
      {text('Pitch', s.pitch)}
      {text('Ask', s.ask)}
      {blocks('Gatekeeper', s.gatekeeper)}
      {blocks('Objections', s.objections.map((o) => ({ label: o.label, text: o.response + (o.followUpQuestion ? `\n→ ${o.followUpQuestion}` : '') })))}
      {blocks('Live voicemails', s.voicemailsLive.map((v) => ({ label: `${v.label} (${v.situation})`, text: v.text })))}
      {blocks('Drop scripts', s.voicemailsDrop.map((v) => ({ label: `${v.label} (${v.situation})`, text: v.text })))}
      {blocks('Emails', s.emails.map((e) => ({ label: `${e.situation}: ${e.subject}`, text: e.body })))}
      {blocks('Disqualifiers', s.disqualifiers.map((d, i) => ({ label: String(i + 1), text: d })))}
      {blocks('Compliance notes', s.complianceNotes.map((d, i) => ({ label: String(i + 1), text: d })))}
    </div>
  );
}
